import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  billingAddress: {
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    email: '',
    contactNumber: '',
  },
  shippingAddress: {
    firstName: '',
    lastName: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    postcode: '',
    email: '',
    contactNumber: '',
  },
  selectedDeliveryOption: '',
  useShippingAsBilling: false,
  discount: 0,
  subtotal: 0,
  shipping: {
    LKR: 350,
    USD: 5
  },
  total: 0,
  currency: 'LKR',
  checkoutData: null,
  paymentMethod: 'Payhere'
};

const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setBillingAddress(state, action) {
      state.billingAddress = action.payload;
      if (state.useShippingAsBilling) {
        state.shippingAddress = action.payload;
      }
    },
    
    setShippingAddress(state, action) {
      state.shippingAddress = action.payload;
    },
    
    setDeliveryOption(state, action) {
      state.selectedDeliveryOption = action.payload;
    },
    
    setUseShippingAsBilling(state, action) {
      state.useShippingAsBilling = action.payload;
      if (action.payload) {
        state.shippingAddress = state.billingAddress;
      }
    },
    
    setPaymentMethod(state, action) {
      state.paymentMethod = action.payload;
    },

    setCurrency(state, action) {
      state.currency = action.payload;
      // Update shipping cost when currency changes
      state.total = state.subtotal + state.shipping[action.payload] - state.discount;
    },
    
    setSubtotal(state, action) {
      state.subtotal = action.payload;
      state.total = state.subtotal + state.shipping[state.currency] - state.discount;
    },
    
    applyDiscount(state, action) {
      state.discount = action.payload;
      state.total = state.subtotal + state.shipping[state.currency] - state.discount;
    },
    
    calculateTotal(state) {
      state.total = state.subtotal + state.shipping[state.currency] - state.discount;
    },

    initializeCheckout(state, action) {
      const { items, currency } = action.payload;
      state.currency = currency;
      state.subtotal = items.reduce((total, item) => {
        const price = currency === 'LKR' 
          ? (item.OfferPrice ? parseFloat(item.OfferPrice) : parseFloat(item.Price))
          : (item.InternationalOfferPrice 
              ? parseFloat(item.InternationalOfferPrice) 
              : parseFloat(item.InternationalPrice || item.Price));
        return total + (price * item.quantity);
      }, 0);
      state.total = state.subtotal + state.shipping[currency] - state.discount;
    },
    
    updateQuantity(state, action) {
      const { items, currency } = action.payload;
      state.subtotal = items.reduce((total, item) => {
        const price = currency === 'LKR'
          ? (item.OfferPrice ? parseFloat(item.OfferPrice) : parseFloat(item.Price))
          : (item.InternationalOfferPrice 
              ? parseFloat(item.InternationalOfferPrice) 
              : parseFloat(item.InternationalPrice || item.Price));
        return total + (price * item.quantity);
      }, 0);
      state.total = state.subtotal + state.shipping[currency] - state.discount;
    },

    setCheckoutData(state, action) {
      state.checkoutData = action.payload;
    },

    clearDiscount(state) {
      state.discount = 0;
      state.total = state.subtotal + state.shipping[state.currency];
    },

    resetCheckout: (state) => {
      return {
        ...initialState,
        currency: state.currency // Preserve the current currency setting
      };
    }
  },
});

export const {
  setBillingAddress,
  setShippingAddress,
  setDeliveryOption,
  setUseShippingAsBilling,
  setPaymentMethod,
  setCurrency,
  setSubtotal,
  applyDiscount,
  calculateTotal,
  initializeCheckout,
  updateQuantity,
  setCheckoutData,
  clearDiscount,
  resetCheckout
} = checkoutSlice.actions;

// Selectors
export const selectBillingAddress = state => state.checkout.billingAddress;
export const selectShippingAddress = state => state.checkout.shippingAddress;
export const selectDeliveryOption = state => state.checkout.selectedDeliveryOption;
export const selectUseShippingAsBilling = state => state.checkout.useShippingAsBilling;
export const selectPaymentMethod = state => state.checkout.paymentMethod;
export const selectSubtotal = state => state.checkout.subtotal;
export const selectTotal = state => state.checkout.total;
export const selectDiscount = state => state.checkout.discount;
export const selectCurrency = state => state.checkout.currency;
export const selectShipping = state => state.checkout.shipping[state.checkout.currency];

export default checkoutSlice.reducer;